import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Sparkles, PenLine, SearchX, Layers, CalendarClock, AlertTriangle, Lightbulb, Send, ChevronRight } from "lucide-react";
import {
  channels as channelsApi,
  content as contentApi,
  type Channel,
  type ContentInput,
  type ContentItem,
  type ContentStatus,
  type CreateContentInput,
} from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ContentList } from "@/components/ContentList";
import { ContentEditor } from "@/components/ContentEditor";
import { PageHeading } from "@/components/PageHeading";
import { EmptyState } from "@/components/EmptyState";
import { MetricCard } from "@/components/MetricCard";
import {
  ARCHIVED_STATUSES,
  DEFAULT_STATUSES,
  buildContentSummary,
  fmtDate,
  formatLabel,
  groupContent,
  type ContentGroup,
  nextAction,
  pillarLabel,
  renderContentText,
  statusLabel,
  type GroupBy,
} from "@/lib/content";
import { parseContent } from "@/lib/parseContent";
import { useAuth } from "@/lib/AuthContext";
import { can } from "@/lib/permissions";
import { cn } from "@/lib/utils";

type ContentCache = { content: ContentItem[] };

export function PlannerPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canEdit = can(user, "edit_operational");

  const contentQ = useQuery({ queryKey: ["content"], queryFn: () => contentApi.list() });
  const channelsQ = useQuery({ queryKey: ["channels"], queryFn: () => channelsApi.list() });

  const [statusFilter, setStatusFilter] = useState<Set<ContentStatus>>(
    () => new Set(DEFAULT_STATUSES),
  );
  const [showArchived, setShowArchived] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupBy>("channel");
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState<ContentItem | null>(null);

  const createMut = useMutation({
    mutationFn: (data: CreateContentInput) => contentApi.create(data),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["content"] }),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: ContentInput }) =>
      contentApi.update(id, patch),
    onMutate: async ({ id, patch }) => {
      await queryClient.cancelQueries({ queryKey: ["content"] });
      const prev = queryClient.getQueryData<ContentCache>(["content"]);
      if (prev) {
        queryClient.setQueryData<ContentCache>(["content"], {
          content: prev.content.map((it) => (it.id === id ? { ...it, ...patch } : it)),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["content"], ctx.prev);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["content"] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => contentApi.remove(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ["content"] });
      const prev = queryClient.getQueryData<ContentCache>(["content"]);
      if (prev) {
        queryClient.setQueryData<ContentCache>(["content"], {
          content: prev.content.filter((it) => it.id !== id),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["content"], ctx.prev);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["content"] }),
  });

  const reorderMut = useMutation({
    mutationFn: (ids: number[]) => contentApi.reorder(ids),
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: ["content"] });
      const prev = queryClient.getQueryData<ContentCache>(["content"]);
      if (prev) {
        const order = new Map(ids.map((id, i) => [id, i]));
        queryClient.setQueryData<ContentCache>(["content"], {
          content: prev.content.map((it) =>
            order.has(it.id) ? { ...it, sort_order: order.get(it.id)! } : it,
          ),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["content"], ctx.prev);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["content"] }),
  });

  const items = useMemo(() => contentQ.data?.content ?? [], [contentQ.data]);
  const channels = useMemo(() => channelsQ.data?.channels ?? [], [channelsQ.data]);

  const summary = useMemo(() => buildContentSummary(items), [items]);

  const visibleItems = useMemo(
    () =>
      items.filter(
        (it) =>
          statusFilter.has(it.status) ||
          (showArchived && (ARCHIVED_STATUSES as ContentStatus[]).includes(it.status)),
      ),
    [items, statusFilter, showArchived],
  );
  const groups = useMemo(() => groupContent(visibleItems, groupBy), [visibleItems, groupBy]);

  function toggleStatus(s: ContentStatus) {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) {
        if (next.size === 1) return prev; // never let the filter go empty
        next.delete(s);
      } else {
        next.add(s);
      }
      return next;
    });
  }

  const isDefaultFilter =
    !showArchived &&
    statusFilter.size === DEFAULT_STATUSES.length &&
    DEFAULT_STATUSES.every((s) => statusFilter.has(s));
  const isFocused = (s: ContentStatus) =>
    !showArchived && statusFilter.size === 1 && statusFilter.has(s);
  function focusStatus(s: ContentStatus) {
    setShowArchived(false);
    setStatusFilter(new Set([s]));
  }
  function resetFilters() {
    setShowArchived(false);
    setStatusFilter(new Set(DEFAULT_STATUSES));
  }

  // Manual add — a blank item you then fill in inline (not just quick-add).
  function newItem(extra: Partial<CreateContentInput>) {
    const status = ([...statusFilter][0] ?? "idea") as ContentStatus;
    createMut.mutate({ title: "New idea", status, ...extra });
  }
  function addToGroup(g: ContentGroup) {
    if (groupBy === "channel") return newItem({ channel_id: g.items[0]?.channel_id ?? null });
    if (groupBy === "pillar") return newItem({ pillar: g.items[0]?.pillar ?? null });
    if (groupBy === "piece") return newItem({ piece: g.items[0]?.piece ?? null });
    return newItem({});
  }

  async function copyText() {
    try {
      await navigator.clipboard.writeText(renderContentText(groups));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — no-op */
    }
  }

  const loading = contentQ.isLoading || channelsQ.isLoading;

  return (
    <div className="space-y-5">
      <PageHeading
        title="Planner"
        sub="Every idea, draft, and scheduled post across your integrations — one board."
        eyebrow="Kovach Consulting · Today"
      />

      {canEdit ? (
        <QuickAddBar
          channels={channels}
          pending={createMut.isPending}
          onAdd={(data) => createMut.mutate(data)}
        />
      ) : null}

      <NextActionBanner items={items} onOpen={setEditing} />

      {items.length > 0 ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <MetricCard
            icon={Layers}
            tone="muted"
            label="Total"
            value={summary.total}
            onClick={resetFilters}
            active={isDefaultFilter}
          />
          <MetricCard
            icon={CalendarClock}
            tone="primary"
            label="Upcoming"
            value={summary.upcoming}
            caption={
              summary.unscheduled > 0
                ? `${summary.unscheduled} ready, no date set`
                : "scheduled"
            }
            onClick={() => focusStatus("scheduled")}
            active={isFocused("scheduled")}
          />
          {summary.overdue > 0 ? (
            <MetricCard
              icon={AlertTriangle}
              tone="destructive"
              label="Overdue"
              value={summary.overdue}
              caption="past due"
              onClick={() => focusStatus("scheduled")}
              active={isFocused("scheduled")}
            />
          ) : null}
          <MetricCard
            icon={PenLine}
            tone="accent"
            label="Drafting"
            value={summary.byStatus.drafting}
            onClick={() => focusStatus("drafting")}
            active={isFocused("drafting")}
          />
          <MetricCard
            icon={Lightbulb}
            tone="secondary"
            label="Ideas"
            value={summary.byStatus.idea}
            onClick={() => focusStatus("idea")}
            active={isFocused("idea")}
          />
          <MetricCard
            icon={Send}
            tone="muted"
            label="Published"
            value={summary.byStatus.published}
            onClick={() => focusStatus("published")}
            active={isFocused("published")}
          />
        </div>
      ) : null}

      {/* Controls */}
      <Card className="no-print">
        <CardContent className="flex flex-col gap-3 p-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="pr-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
              Show
            </span>
            {DEFAULT_STATUSES.map((s) => (
              <Pill key={s} active={statusFilter.has(s)} onClick={() => toggleStatus(s)}>
                {statusLabel(s)}
              </Pill>
            ))}
            <Pill active={showArchived} onClick={() => setShowArchived((v) => !v)}>
              Archived
            </Pill>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="pr-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
              Group
            </span>
            <GroupToggle value={groupBy} onChange={setGroupBy} />
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => void copyText()}
              disabled={groups.length === 0}
            >
              {copied ? "Copied" : "Copy as text"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => window.print()}
            >
              Export PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* List / empties */}
      {loading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={PenLine}
          label="No content yet"
          hint={
            canEdit
              ? "Capture your first idea above — try “draft a thread about risk on LinkedIn tomorrow.”"
              : "Nothing has been planned yet."
          }
        />
      ) : groups.length === 0 ? (
        <EmptyState
          icon={SearchX}
          tone="muted"
          label="Nothing matches"
          hint="No content fits the current filters. Toggle a status back on, or show archived."
        />
      ) : (
        <ContentList
          groups={groups}
          channels={channels}
          canEdit={canEdit}
          groupBy={groupBy}
          onUpdate={(id, patch) => updateMut.mutateAsync({ id, patch })}
          onDelete={(id) => deleteMut.mutateAsync(id)}
          onAddToGroup={canEdit ? addToGroup : undefined}
          onOpenItem={setEditing}
          onReorder={canEdit ? (ids) => reorderMut.mutate(ids) : undefined}
        />
      )}

      {editing ? (
        <ContentEditor
          item={editing}
          channels={channels}
          canEdit={canEdit}
          onClose={() => setEditing(null)}
          onUpdate={(id, patch) => updateMut.mutateAsync({ id, patch })}
          onDelete={(id) => deleteMut.mutateAsync(id)}
        />
      ) : null}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Quick-add: single-line natural-language capture
// ----------------------------------------------------------------------------

const CHIP_TONE = {
  channel: "border-primary/40 bg-primary/10 text-foreground",
  pillar: "border-secondary/40 bg-secondary/10 text-foreground",
  format: "border-border bg-muted text-muted-foreground",
  date: "border-accent/50 bg-accent/15 text-foreground",
} as const;

function QuickAddBar({
  channels,
  onAdd,
  pending,
}: {
  channels: Channel[];
  onAdd: (data: CreateContentInput) => void;
  pending: boolean;
}) {
  const [text, setText] = useState("");
  const parsed = useMemo(() => parseContent(text, channels), [text, channels]);
  const title = parsed.title.trim();
  const canAdd = text.trim() !== "";

  function submit() {
    if (!canAdd || pending) return;
    onAdd({
      title: title || text.trim(),
      channel_id: parsed.channelId,
      pillar: parsed.pillar,
      format: parsed.format,
      scheduled_for: parsed.scheduledFor,
      status: "idea",
    });
    setText("");
  }

  const chips: { key: string; label: string; tone: string }[] = [];
  if (parsed.channelName) chips.push({ key: "ch", label: parsed.channelName, tone: CHIP_TONE.channel });
  if (parsed.pillar) chips.push({ key: "pl", label: pillarLabel(parsed.pillar), tone: CHIP_TONE.pillar });
  chips.push({ key: "fmt", label: formatLabel(parsed.format), tone: CHIP_TONE.format });
  if (parsed.scheduledFor != null) {
    chips.push({ key: "date", label: fmtDate(parsed.scheduledFor), tone: CHIP_TONE.date });
  }

  return (
    <Card className="no-print">
      <CardContent className="p-3">
        <div className="flex items-center gap-2">
          <Plus size={16} className="shrink-0 text-muted-foreground" aria-hidden />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
            placeholder='Add content — e.g. "draft a thread about risk on LinkedIn tomorrow"'
            className="h-8 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          <Button size="sm" onClick={submit} disabled={!canAdd || pending}>
            {pending ? "Adding…" : "Add"}
          </Button>
        </div>
        {canAdd ? (
          <div className="flex flex-wrap items-center gap-1.5 pl-6 pt-2">
            <span className="text-xs font-medium text-foreground">
              {title || <span className="text-muted-foreground">needs a title</span>}
            </span>
            {chips.map((c) => (
              <span
                key={c.key}
                className={cn("rounded-full border px-2 py-0.5 text-[11px]", c.tone)}
              >
                {c.label}
              </span>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Next-action banner
// ----------------------------------------------------------------------------

function NextActionBanner({
  items,
  onOpen,
}: {
  items: ContentItem[];
  onOpen?: (item: ContentItem) => void;
}) {
  const next = useMemo(() => nextAction(items), [items]);
  if (!next) return null;
  const clickable = Boolean(onOpen && next.item);
  const inner = (
    <CardContent className="flex items-center gap-3 p-4">
      <Sparkles size={20} className="shrink-0 text-primary" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">{next.text}</div>
        {next.detail ? <div className="text-xs text-muted-foreground">{next.detail}</div> : null}
      </div>
      {clickable ? <ChevronRight size={16} className="shrink-0 text-muted-foreground" aria-hidden /> : null}
    </CardContent>
  );
  if (clickable) {
    return (
      <Card
        role="button"
        tabIndex={0}
        onClick={() => onOpen!(next.item!)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen!(next.item!);
          }
        }}
        className="cursor-pointer border-l-4 border-l-primary transition-colors hover:bg-muted/40"
      >
        {inner}
      </Card>
    );
  }
  return <Card className="border-l-4 border-l-primary">{inner}</Card>;
}

// ----------------------------------------------------------------------------
// Small bits
// ----------------------------------------------------------------------------

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
        active
          ? "border-foreground bg-foreground text-background"
          : "border-border text-muted-foreground hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}

const GROUP_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: "channel", label: "Integration" },
  { value: "pillar", label: "Pillar" },
  { value: "month", label: "Month" },
  { value: "piece", label: "Piece" },
];

function GroupToggle({
  value,
  onChange,
}: {
  value: GroupBy;
  onChange: (g: GroupBy) => void;
}) {
  return (
    <div className="inline-flex rounded-md border p-0.5">
      {GROUP_OPTIONS.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded px-2 py-1 text-xs font-medium transition-colors",
            value === o.value
              ? "bg-foreground text-background"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
